import {
  barkText,
  buildCollisionWorld,
  buildMapCollisionWorld,
  combineMoveMods,
  createFullNeeds,
  DEFAULT_ENV,
  DEFAULT_SKILL_MODS,
  getItemDef,
  getMapById,
  hasMap,
  LIMB_IDS,
  limbMaskHas,
  mapCollisionSolids,
  moveModsFromLegs,
  moveModsFromNeeds,
  moveModsFromWeight,
  TICK_RATE,
  type AABB,
  type MapDef,
  type MoveMods,
  type SkillMods,
  type Vec3,
} from "@rietz/shared";
import * as THREE from "three";
import { GameAudio } from "./audio/synth.js";
import { authenticateForGame, logoutFromGame, type AuthAccount } from "./auth.js";
import { reloadIfStale, watchForStaleBuild } from "./buildCheck.js";
import { DebugOverlay } from "./debug/overlay.js";
import { DebugTools } from "./debug/tools.js";
import { AimSystem, type AimContext } from "./game/aim.js";
import { BirdFlush } from "./game/birds.js";
import { VisualBullets } from "./game/bullets.js";
import { DroppedItems } from "./game/droppedItems.js";
import { FireControl, confirmHit } from "./game/fireControl.js";
import { Fx } from "./game/fx.js";
import { GlassState } from "./game/glass.js";
import { GrenadeControl } from "./game/grenades.js";
import { RangeControl } from "./game/hideout/range.js";
import { HideoutSession } from "./game/hideout/session.js";
import { Interact } from "./game/interact.js";
import { InventoryController } from "./game/inventory.js";
import { KILLCAM_BODY_Y, KILLCAM_MS } from "./game/killcam.js";
import { MetaInventoryController } from "./game/metaInventory.js";
import { OpIds } from "./game/opIds.js";
import { RemoteAvatars } from "./game/remotes.js";
import { SocialController } from "./game/social.js";
import { TacticalFx } from "./game/tacticalFx.js";
import { WorldView } from "./game/world.js";
import { installKeyboardLock } from "./input/fullscreen.js";
import { Input } from "./input/input.js";
import { GameLoop, type CombatSystems } from "./loop.js";
import { NetClock } from "./net/clock.js";
import { EntityInterpolator } from "./net/interpolation.js";
import { httpOriginFor, MapLoader, yieldToDisplay } from "./net/mapLoader.js";
import { Prediction } from "./net/prediction.js";
import { GameSocket, type NetStatus } from "./net/socket.js";
import { Viewmodel } from "./render/viewmodel.js";
import { useSettings } from "./settings.js";
import { setUiActions, type UiActions } from "./ui/actions.js";
import { LIMB_LABELS } from "./ui/limbs.js";
import { mountHud } from "./ui/mount.js";
import { TownMap } from "./ui/townMap.js";
import {
  noteTeammate,
  noteTeammateDeath,
  pushSnapshot,
  resetHud,
  resetRaidHud,
  setAmmo,
  setConnected,
  setDeath,
  setExtract,
  setFlash,
  setLeaderboard,
  setMe,
  setMode,
  setQueue,
  setRaidEndsAt,
  setScreen,
  setSettingsOpen,
  setSummary,
  useHud,
  type DeathInfo,
  type LeaderboardRow,
  type Screen,
  type ServerMode,
} from "./ui/store.js";

/**
 * Boot. An online client boots into nothing — empty collision world, empty
 * scene, no pointer — until `welcome` names a map and it is downloaded and
 * installed (standing in Testfield while the real map downloaded meant
 * predicting against the wrong world). Testfield is the explicit `?offline=1`
 * sandbox.
 *
 * URL parameters:
 *   ?name=<string>   display name on direct/offline servers
 *   ?server=<url>    WebSocket endpoint or host:port (default: `DEFAULT_SERVER_URL`)
 *   ?offline=1       no network; single-player prediction on a static map
 *   ?map=<id>        which static map the offline sandbox uses (only `testfield`)
 *   ?debug=1         scene helpers and a printout of the F3–F6 debug keybinds
 *                    (the tool belt itself is always wired up — `debug/tools.ts`)
 */

/**
 * Where to connect when `?server=` says nothing. Development (Vite on 5173/5174,
 * or `file://`): the game server is a separate process on port 8081 of the same
 * host, so a LAN device still finds it. Production: the page came from the game
 * server (directly or through the TLS proxy), so the socket is on the SAME
 * ORIGIN — the proxy only publishes 443, an `https:` page blocks `ws://`, and
 * same origin keeps the `SameSite=Strict` session cookie on the upgrade.
 */
const DEFAULT_SERVER_URL = ((): string => {
  const devServed = location.host === "" || location.port === "5173" || location.port === "5174";
  if (devServed) return `ws://${location.hostname || "localhost"}:8081`;
  return `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}`;
})();

/**
 * Bounds for the placeholder collision world an online client boots with;
 * `swapIn` replaces it wholesale.
 */
const VOID_BOUNDS: AABB = {
  min: { x: -8, y: -1, z: -8 },
  max: { x: 8, y: 8, z: 8 },
};

/** Where prediction is parked while there is no world. Nobody ever moves here. */
const VOID_SPAWN: Vec3 = { x: 0, y: 1, z: 0 };

/**
 * Where the two legs sit in `YouSnap.hp` (`LIMB_IDS` order), resolved once so
 * appending a limb cannot repoint them.
 */
const LEG_L_HP_INDEX = LIMB_IDS.indexOf("legL");
const LEG_R_HP_INDEX = LIMB_IDS.indexOf("legR");

/**
 * What "no needs block on the wire" means: both meters full. Hoisted so a
 * pre-v26 server's snapshots allocate nothing. Never mutated.
 */
const FULL_NEEDS = createFullNeeds();

/**
 * `account` is the authenticated session, absent on direct/offline. The display
 * name is what the world calls the player; the USERNAME is who they are
 * (`HudState.me`).
 */
function boot(account?: AuthAccount): void {
  const params = new URLSearchParams(window.location.search);
  const offline = isTruthy(params.get("offline"));
  const debug = isTruthy(params.get("debug"));
  const rawName = params.get("name");
  const name = account?.name ?? sanitizeName(rawName);
  const serverUrl = resolveServerUrl(params.get("server"));

  // Written once and not cleared by a disconnect: a fact about the browser
  // session.
  setMe(account?.username ?? null);

  const canvas = document.getElementById("view");
  const overlayEl = document.getElementById("overlay");
  const scrim = document.getElementById("scrim");
  const uiEl = document.getElementById("ui");
  if (!(canvas instanceof HTMLCanvasElement) || overlayEl === null) {
    throw new Error("boot: #view canvas or #overlay missing from the document");
  }

  // The scrim doubles as the loading display, so its writers come first.
  const setScrimStatus = (text: string): void => {
    const title = document.getElementById("scrim-title");
    if (title === null) return;
    title.textContent = text;
  };

  /** Shows the scrim's download bar at `frac` (0..1); `null` hides it. */
  const setScrimProgress = (frac: number | null): void => {
    const bar = document.getElementById("scrim-progress");
    const fill = document.getElementById("scrim-progress-fill");
    if (bar === null || fill === null) return;
    bar.hidden = frac === null;
    if (frac !== null) {
      fill.style.width = `${(Math.min(1, Math.max(0, frac)) * 100).toFixed(1)}%`;
    }
  };

  const overlay = new DebugOverlay(overlayEl);
  const view = new WorldView({ canvas, debug });
  const input = new Input({ canvas, scrim });
  // Online: nothing to render until `swapIn`. Offline already has a world.
  if (!offline) {
    input.setMapReady(false);
    setScrimStatus("Connecting…");
  }
  const clock = new NetClock();
  const interpolator = new EntityInterpolator();
  // The camera for distance-faded name tags; the collision getter so a remote
  // laser stops at the wall.
  const remotes = new RemoteAvatars(view.scene, view.camera, () => collision);

  // The first-person gun is a second render pass owned by `WorldView`, so it
  // cannot clip into geometry however close to a wall the player stands.
  const viewmodel = new Viewmodel({ mainCamera: view.camera });
  view.setOverlay(viewmodel);

  const fx = new Fx({ scene: view.scene, muzzle: viewmodel.muzzle });
  // Its own pool rather than a fifth effect in `Fx`: not combat, own clock.
  const birds = new BirdFlush(view.scene);
  const audio = new GameAudio();
  // Volume is pushed at the audio (`synth.ts` imports nothing of ours): once
  // now — the want is remembered until there is an AudioContext — and per
  // change.
  audio.setMasterVolume(useSettings.getState().volume);
  useSettings.subscribe((s, prev) => {
    if (s.volume !== prev.volume) audio.setMasterVolume(s.volume);
  });
  // Browsers will not start an AudioContext without a user gesture; the click
  // that grabs the pointer is one, and it is the first thing every session does.
  canvas.addEventListener("mousedown", () => {
    audio.resume();
  });

  // THE world, or none. Online it is `null` until `welcome` and the download;
  // offline it is the static sandbox and never changes.
  let map: MapDef | null = offline ? getMapById(initialMapId(params.get("map"))) : null;
  // Hard solids keep index alignment with `map.solids`; generated maps add
  // their terrain heightfield. With no map it is an empty world in a void.
  let collision =
    map === null
      ? buildCollisionWorld([], VOID_BOUNDS)
      : buildMapCollisionWorld(map);
  /** The boxes `collision` was built from — glass appended after the solids.
   *  Rebuilt wherever `collision` is, and handed to `stepProjectile`. */
  let solids = map === null ? [] : mapCollisionSolids(map);
  if (map !== null) view.loadMap(map);

  const tools = new DebugTools({ view, input, overlay, showHelp: debug });
  if (map !== null) tools.setMap(map);

  // The M-key town map. Owns its own keybind and its own resize handling, is
  // painted once per map, and does no work at all while it is closed.
  const townMap = new TownMap();
  if (map !== null) townMap.setMap(map);

  const spawn: Vec3 = map?.playerSpawns[0] ?? VOID_SPAWN;
  const prediction = new Prediction(collision, spawn);

  /**
   * Every round on screen, flown with the shared ballistics — `stepProjectile`
   * is the very function the server steps, so what the player watches is the
   * trajectory the server resolves.
   */
  const bullets = new VisualBullets({
    fx,
    // Impacts and near misses come off the flown rounds, because this is the
    // only place that knows where they are and what they hit.
    audio,
    // Getters throughout: the collision world and the map behind it are both
    // swapped on a map change, and the poses array is refilled every frame.
    world: () => collision,
    // Index-aligned with the collision world: hard solids, then one box per
    // window pane, so a tracer flies THROUGH glass instead of stopping on an
    // index `stepProjectile` cannot name.
    solids: () => solids,
    poses: () => interpolator.lastPoses,
    // For the hideout's range — a scoreboard, not hit registration. Reaching
    // forward to a `const` declared below is safe: impacts only come from
    // inside
    // a rendered frame, long after boot.
    onImpact: (pos, local) => {
      range.onImpact(pos, local);
    },
  });

  /**
   * The local player's lamp and laser. Constructed here because it needs the
   * `() => collision` getter; lives in the WORLD scene because a light parented
   * to the first-person gun would light the gun and nothing else. See its header.
   */
  const tacticalFx = new TacticalFx({ scene: view.scene, world: () => collision });

  let status: NetStatus | "offline" = offline ? "offline" : "idle";
  let playerId = "";

  /**
   * Names seen this session, so a `death` event's `killerId` can be shown as a
   * name. Filled from `playerJoined` and every snapshot that carries one — which
   * is what covers scavs, who never join.
   */
  const names = new Map<string, string>();
  const displayName = (id: string): string => names.get(id) ?? id;

  const socket = offline ? null : new GameSocket({ url: serverUrl, name });

  /** Mirrors the store's `dead`, so the transitions can be acted on once each. */
  let wasDead = false;
  /** Mirrors the inventory screen, for the same reason. */
  let invOpen = false;
  /** Mirrors the in-raid settings overlay, for the same reason again. */
  let settingsOpen = false;
  /**
   * ...and the hideout's own overlays — the fourth mirror, written only by
   * `setHideoutUiOpen`, so `applyLockAllowed` stays a read of four flags.
   */
  let hideoutUiOpen = false;

  /**
   * Which kind of server, decided by whichever message lands first after
   * `hello`. Sticky (see `ServerMode`); `null` offline.
   */
  let mode: ServerMode | null = null;
  const noteMode = (m: ServerMode): void => {
    if (mode !== null) return;
    mode = m;
    setMode(m);
  };

  /**
   * Mirrors the store's `screen` so the loop can branch without subscribing.
   * Offline is a raid from the first frame.
   */
  let screen: Screen = offline ? "raid" : "connecting";
  if (offline) {
    setScreen("raid");
    // `goScreen` is what normally arms the ambience, and offline it never
    // fires — the sandbox is already in the raid on the first frame.
    audio.setAmbience(true);
  }

  /** True while one of the between-raids screens owns the display. */
  const inMetaScreen = (): boolean =>
    screen === "menu" || screen === "queue" || screen === "summary";

  /**
   * True while between raids in the broad sense — the menu screens and the
   * walkable hideout: no raid inventory to open, crate to close or bandage to
   * apply. The cellar is a first-person scene the raid's verbs say nothing about.
   */
  const inMenuLand = (): boolean => inMetaScreen() || screen === "hideout";

  /**
   * Where the cellar's verbs are allowed: the menu tab that draws it and the
   * room itself (not the queue or the summary).
   */
  const canTouchHideout = (): boolean => screen === "menu" || screen === "hideout";

  /**
   * Five things want the cursor — death (a DOM respawn button), the inventory
   * (drag and drop), the settings overlay (sliders), the menu screens, and a
   * hideout station's panel — and only one place arbitrates: the pointer is
   * taken back only when NONE of them wants it. `lockAllowed = false` also
   * hides the scrim (`input.ts`), which is right — the menu is drawn there.
   */
  const applyLockAllowed = (): void => {
    input.setLockAllowed(
      !wasDead && !invOpen && !settingsOpen && !inMetaScreen() && !hideoutUiOpen,
    );
  };

  /**
   * A hideout overlay opened or closed — mirror, re-arbitrate, and on close
   * hand the pointer straight back (the request can be refused after an
   * Escape-driven exit; the next canvas click retries).
   */
  const setHideoutUiOpen = (open: boolean): void => {
    if (hideoutUiOpen === open) return;
    hideoutUiOpen = open;
    applyLockAllowed();
    if (!open && !wasDead) input.requestLock();
  };

  /**
   * Shows/hides the in-raid settings overlay — the only writer of that flag.
   * Same shape as the inventory's `onScreenChanged`.
   */
  const showSettings = (open: boolean): void => {
    if (settingsOpen === open) return;
    settingsOpen = open;
    setSettingsOpen(open);
    applyLockAllowed();
    if (!open && !wasDead) input.requestLock();
  };

  /** The one place `screen` moves. Every transition re-arbitrates the cursor. */
  const goScreen = (next: Screen): void => {
    if (screen === next) return;
    screen = next;
    setScreen(next);
    applyLockAllowed();
    // The wind belongs to the valley. Harmless before the first gesture — the
    // want is remembered.
    audio.setAmbience(next === "raid");
  };
  // A tab parked on the menu overnight comes back to whatever was deployed in
  // the meantime — never mid-raid, which `screen` guards. See `buildCheck.ts`.
  if (!offline) watchForStaleBuild(serverUrl, () => screen === "menu");

  /**
   * One op-id counter for the whole connection (raid inventory, menu inventory,
   * traders, social) — all answered by `invResult`.
   */
  const opIds = new OpIds();

  /**
   * What the prediction believes is slowing it down. Injury half off every
   * snapshot, encumbrance half off every inventory change; kept apart and
   * recombined when either moves. Offline stays at the healthy default.
   */
  let healthMods: MoveMods = { speedMult: 1, accelMult: 1 };
  let carriedKg = 0;
  /**
   * The other snapshot-driven slowdown: hunger and thirst. A different curve
   * from `healthMods`, so kept apart. Stays at the fed identity offline.
   */
  let needsMods: MoveMods = moveModsFromNeeds(FULL_NEEDS);
  /**
   * What the skills are worth — a raid-long constant written from `welcome`,
   * reset when the raid or connection ends.
   */
  let skillMods: SkillMods = DEFAULT_SKILL_MODS;
  /**
   * The halves recombined. `carryMult` goes INTO the encumbrance ramp;
   * `staminaRegenMult` is spread on only when not exactly 1 — a SHAPE guarantee
   * so a default player hands `combineMoveMods` the two-key object the parity
   * tests were written against. `needsMods` folds in under the same guarantee
   * (a fed body is the two-field identity), beside the injuries because both
   * are things the server is already applying.
   */
  const refreshMoveMods = (): void => {
    prediction.setMods(
      combineMoveMods(combineMoveMods(healthMods, needsMods), {
        ...moveModsFromWeight(carriedKg, skillMods.carryMult),
        ...(skillMods.staminaRegenMult !== 1
          ? { staminaRegenMult: skillMods.staminaRegenMult }
          : {}),
      }),
    );
  };

  /**
   * The inventory, the loot panel and the meds. Reached through `UiActions`, so
   * nothing in `ui/` touches the socket.
   */
  const inventory = new InventoryController({
    socket,
    clock,
    offline,
    opIds,
    onWeightChanged: (kg) => {
      carriedKg = kg;
      refreshMoveMods();
    },
    onScreenChanged: (open) => {
      invOpen = open;
      applyLockAllowed();
      // Closing hands the pointer back (may be refused after an Escape exit;
      // the next click retries).
      if (!open && !wasDead) input.requestLock();
    },
  });

  /**
   * The between-raids twin: kit, stash, counter. Owns the store's inventory
   * slots only while the menu is up (`setActive`).
   */
  const meta = new MetaInventoryController({ socket, opIds });

  /**
   * Friends and party — the third drawer on the op-id counter, and the one that
   * predicts nothing.
   */
  const social = new SocialController({ socket, opIds });

  /**
   * The frozen death camera while it runs — the killer whose avatar is hidden
   * and when the picture comes back. `null` when the camera is our own.
   */
  let killcam: { killerId: string; untilMs: number } | null = null;

  /** A raid summary held back until the killcam is done. See `onRaidEnd`. */
  let summaryTimer: number | null = null;

  /** Gives the camera back and draws the killer again. Idempotent. */
  const endKillcam = (): void => {
    if (killcam === null) return;
    remotes.setHidden(killcam.killerId, false);
    killcam = null;
    loop.clearKillcam();
  };

  /** Drops a summary still waiting on a killcam that no longer matters. */
  const cancelSummaryDefer = (): void => {
    if (summaryTimer === null) return;
    clearTimeout(summaryTimer);
    summaryTimer = null;
  };

  /**
   * Death releases the mouse: the respawn button is a real DOM button. Capture
   * is handed back on respawn.
   */
  const setDeadUi = (dead: boolean): void => {
    if (dead === wasDead) return;
    wasDead = dead;
    applyLockAllowed();
    // Back among the living (respawn, or a raid torn down under us): the
    // camera is ours again.
    if (!dead) endKillcam();
    // Driven from the TRANSITION, not the `death` event, so it covers a dropped
    // frame or joining a raid already dead.
    if (dead) {
      inventory.onDeath();
      // Escape belongs to the death overlay now.
      showSettings(false);
    }
  };

  // Recoil and sway as two angles the camera AND the fired direction both add —
  // one object, two readers, so sight picture and bullet cannot disagree.
  const aim = new AimSystem();

  const fireControl = new FireControl({
    input,
    prediction,
    clock,
    viewmodel,
    aim,
    fx,
    audio,
    socket,
    bullets,
    inventory,
  });

  /**
   * The G key and every grenade in the air — the other half of what the hands
   * do, taking the same eye, clock, inventory and viewmodel.
   */
  const grenades = new GrenadeControl({
    scene: view.scene,
    input,
    prediction,
    clock,
    socket,
    inventory,
    viewmodel,
    fx,
    audio,
    world: () => collision,
  });

  /**
   * Which windows are broken. The server decides; this applies the answer to
   * the collision world, the instanced panes and the effects.
   */
  const glassState = new GlassState({
    map: () => map,
    collision: () => collision,
    glass: () => view.glass,
    fx,
    audio,
  });

  /** Everything lying on the ground, and the `E` key that reaches for it. */
  const droppedItems = new DroppedItems(view.scene);
  const interact = new Interact({
    input,
    inventory,
    droppedItems,
    map: () => map,
    poses: () => interpolator.lastPoses,
    glassBroken: (id) => glassState.isBroken(id),
    // Offline there is nobody to ask, and nothing is predicted.
    glass: (id, act) => {
      socket?.sendGlass(id, act);
    },
  });

  /**
   * The hideout, walked (`game/hideout/`). Two objects with two lifetimes: the
   * range exists from boot (the bullet pool reports every impact to it); the
   * session owns the map, stations and overlays. Both idle until `enterHideout`;
   * neither touches the socket.
   */
  const range = new RangeControl({
    input,
    prediction,
    viewmodel,
    aim,
    fx,
    audio,
    bullets,
  });

  const hideout = new HideoutSession({
    view,
    input,
    prediction,
    range,
    tools,
    // Read fresh: a cached profile would keep drawing a cellar whose generator
    // was switched off two profiles ago.
    profile: () => useHud.getState().profile,
    profileAtMs: () => useHud.getState().profileAtMs,
    onOverlayChanged: setHideoutUiOpen,
    // Bullets, tactical light and remotes read the world through `() =>
    // collision`, so a rebuilt shelter repoints it. Rounds in the air were
    // fired
    // at geometry that no longer exists, as after a map swap.
    onWorldChanged: (world) => {
      collision = world;
      bullets.clear();
    },
  });

  if (uiEl !== null) {
    mountHud({
      container: uiEl,
      onRespawn: () => {
        socket?.sendRespawn();
        fireControl.setDead(false);
        grenades.setDead(false);
        // Optimistic: `requestPointerLock` needs this click's user activation.
        // If the server keeps us dead the next snapshot flips it back.
        setDeadUi(false);
        input.requestLock();
      },
    });
  } else {
    console.warn("boot: #ui missing from the document; the HUD is disabled");
  }

  // Keeps exactly one download current and reports progress.
  const mapLoader = new MapLoader(serverUrl);
  let welcomeToken = 0;

  const reportMapFailure = (mapId: string, err: unknown): void => {
    const detail = err instanceof Error ? err.message : String(err);
    console.error(`could not load map "${mapId}": ${detail}`);
    // The socket reconnects on its own; a fresh `welcome` retries the download.
    setScrimStatus("Could not load map — retrying…");
    setScrimProgress(null);
  };

  // -------------------------------------------------------------------------
  // Meta: menu, queue, summary (Phase 6)
  // -------------------------------------------------------------------------

  /**
   * Throw the finished raid away: everything the disconnect path forgets, plus
   * the world itself goes back to the void (standing in a finished raid's
   * village while choosing a loadout would be a lie). Map DATA survives in
   * `MapLoader`'s cache.
   */
  const leaveRaid = (): void => {
    // Any download still in flight belongs to a raid that is over; the token
    // bump is what stops it installing itself on top of the menu.
    welcomeToken += 1;
    playerId = "";
    names.clear();
    clock.reset();
    interpolator.clear();
    remotes.clear();
    bullets.clear();
    // Arcs in the air and a late smoke cloud both belong to the raid.
    grenades.reset();
    fx.clearSmoke();
    // ...and the birds, on exactly the same argument (protocol v28).
    birds.clear();
    droppedItems.clear();
    interact.clearTarget();
    inventory.reset();
    // Leaving the mirror set would refuse the pointer for the whole next raid.
    showSettings(false);
    fireControl.setDead(false);
    setDeadUi(false);
    resetRaidHud();
    // The next raid's `welcome` states its own; a direct raid never sends any.
    skillMods = DEFAULT_SKILL_MODS;
    // Nobody spawns hungry.
    needsMods = moveModsFromNeeds(FULL_NEEDS);
    refreshMoveMods();

    if (map !== null) {
      map = null;
      collision = buildCollisionWorld([], VOID_BOUNDS);
      solids = [];
      glassState.reset();
      view.clearMap();
      tools.setMap(null);
      townMap.setMap(null);
      // The weather ends with the raid — the menu is not standing in the rain.
      // `view.clearMap` resets its own; these two have to be told.
      audio.setEnv(DEFAULT_ENV);
      townMap.setEnv(DEFAULT_ENV);
      prediction.respawn(collision, VOID_SPAWN);
    }
    // The feet just teleported a kilometre; without this the next frame's
    // "distance travelled" would fire a footstep and a full bob cycle.
    haveLastFeet = false;
    // No world means no pointer capture, whatever gets clicked.
    input.setMapReady(false);
    // The loading layer is hidden behind the menu, but it is what the player
    // sees the instant they deploy again, before a new world exists.
    setScrimStatus("Preparing raid…");
    setScrimProgress(null);
  };

  /**
   * Come back up out of the cellar: `leaveRaid`'s world half and none of the
   * rest. Also called on a `welcome` (the leader deployed while we were
   * downstairs) and a dropped connection — both install a different world, so
   * this must run FIRST.
   */
  const exitHideout = (): void => {
    hideout.leave();
    // A round still crossing the bay is about to have nothing to cross.
    bullets.clear();
    collision = buildCollisionWorld([], VOID_BOUNDS);
    solids = [];
    prediction.respawn(collision, VOID_SPAWN);
    // The socket speaks again: what was muted was a body walking around a
    // basement, and the next thing this client does is a raid.
    loop.localOnly = false;
    // The feet just teleported out of a cellar; without this the next frame's
    // "distance travelled" would fire a footstep and a full bob cycle.
    haveLastFeet = false;
    input.setMapReady(false);
    interact.clearTarget();
  };

  /** Show the menu, with the profile's kit and stash in the inventory slots. */
  const enterMenu = (): void => {
    setQueue(null);
    cancelSummaryDefer();
    setSummary(null);
    meta.setActive(true);
    goScreen("menu");
    // Back at the menu is the one place a redeploy can be picked up without
    // costing the player anything — see `buildCheck.ts`.
    if (!offline) void reloadIfStale(serverUrl);
  };

  const uiBridge: UiActions = {
    // The SCREEN decides which inventory a drag belongs to; everything else is
    // identical, which is why the menu reuses the raid's inventory screen.
    tryOp: (op) => {
      if (screen === "menu" || screen === "queue") meta.tryOp(op);
      else inventory.tryOp(op);
    },
    // Raid-only actions: neither the menu nor the cellar has meds, crates or a
    // bag.
    useMed: (iid) => {
      if (!inMenuLand()) inventory.useMed(iid);
    },
    closeContainer: () => {
      if (!inMenuLand()) inventory.closeContainer();
    },
    setInventoryOpen: (open) => {
      if (!inMenuLand()) inventory.setInventoryOpen(open);
    },
    // The overlay's close button; menu-side the settings are a tab.
    setSettingsOpen: (open) => {
      if (!inMenuLand()) showSettings(open);
    },

    deploy: () => {
      if (mode !== "meta" || screen !== "menu") return;
      socket?.sendQueueJoin();
      // The screen changes before the first `queueStatus`; a refused join sends
      // an `error`, which puts us back.
      setQueue(null);
      goScreen("queue");
    },
    leaveQueue: () => {
      if (screen !== "queue") return;
      socket?.sendQueueLeave();
      enterMenu();
    },
    buy: (traderId, defId) => {
      if (screen !== "menu") return;
      meta.buy(traderId, defId);
    },
    sell: (traderId, iid) => {
      if (screen !== "menu") return;
      meta.sell(traderId, iid);
    },
    // Menu-only: the guard keeps the message off the wire rather than earning
    // "in-raid".
    repair: (iid) => {
      if (screen !== "menu") return;
      meta.repair(iid);
    },
    // Menu-only, like the till; the in-raid quest log offers no handover.
    questTurnIn: (questId, iids) => {
      if (screen !== "menu") return;
      meta.questTurnIn(questId, iids);
    },
    // The cellar is between-raids only. Two screens since the hideout became
    // walkable: a station's panel presses these same buttons over the room.
    hideoutBuild: (moduleId) => {
      if (!canTouchHideout()) return;
      meta.hideoutBuild(moduleId);
    },
    hideoutCraft: (craftId) => {
      if (!canTouchHideout()) return;
      meta.hideoutCraft(craftId);
    },
    hideoutCollect: (craftId) => {
      if (!canTouchHideout()) return;
      meta.hideoutCollect(craftId);
    },
    hideoutHarvest: (moduleId) => {
      if (!canTouchHideout()) return;
      meta.hideoutHarvest(moduleId);
    },
    hideoutFuel: (iid) => {
      if (!canTouchHideout()) return;
      meta.hideoutFuel(iid);
    },
    hideoutPower: (on) => {
      if (!canTouchHideout()) return;
      meta.hideoutPower(on);
    },
    /**
     * A finished gym set or shooting drill, paid out as skill progress. Same
     * two screens as the six above, so a score can still be banked after the
     * room closed.
     */
    hideoutTrain: (skill, score) => {
      if (!canTouchHideout()) return;
      meta.hideoutTrain(skill, score);
    },

    // --- The walkable hideout ------------------------------------------------
    /**
     * Down the stairs. `enter` builds the world and repoints `collision`; then
     * mute the socket and move the screen, both only after the build succeeded
     * (`goScreen` must not grab a pointer for a world that does not exist).
     * No ambience — a bunker has no wind.
     */
    enterHideout: () => {
      if (mode !== "meta" || screen !== "menu") return;
      if (!hideout.enter()) return;
      loop.localOnly = true;
      goScreen("hideout");
      // The click is the user gesture; a lock asked for next frame would be
      // refused.
      input.requestLock();
    },
    leaveHideout: () => {
      if (screen !== "hideout") return;
      exitHideout();
      enterMenu();
    },
    /** A station panel's × — the DOM half of the Escape chain in `onEscape`. */
    closeHideoutOverlay: () => {
      if (screen === "hideout") hideout.closeOverlay();
    },
    // The social verbs. Menu-only, except answering an invite is allowed from
    // the queue too — the prompt is drawn over the queue panel.
    friendRequest: (username) => {
      if (screen !== "menu") return;
      social.friendRequest(username);
    },
    friendRespond: (username, accept) => {
      if (screen !== "menu") return;
      social.friendRespond(username, accept);
    },
    friendRemove: (username) => {
      if (screen !== "menu") return;
      social.friendRemove(username);
    },
    partyInvite: (username) => {
      if (screen !== "menu") return;
      social.partyInvite(username);
    },
    partyRespond: (accept) => {
      if (screen !== "menu" && screen !== "queue") return;
      social.partyRespond(accept);
    },
    partyLeave: () => {
      if (screen !== "menu") return;
      social.partyLeave();
    },
    partyKick: (username) => {
      if (screen !== "menu") return;
      social.partyKick(username);
    },
    // The ladder. No socket, no op id, no optimism — an ordinary GET against
    // the same origin the map comes down from, answered into the store.
    fetchLeaderboard: () => {
      if (offline) return;
      void loadLeaderboard(serverUrl);
    },
    logout: () => {
      if (mode !== "meta" || screen === "raid" || screen === "deploying") return;
      void logoutFromGame(serverUrl).finally(() => {
        window.location.reload();
      });
    },
    closeSummary: () => {
      if (screen !== "summary") return;
      leaveRaid();
      enterMenu();
    },
  };
  setUiActions(uiBridge);

  if (socket !== null) {
    socket.on("onStatus", (s) => {
      status = s;
      setConnected(s === "open");
      if (s === "open") {
        clock.resetPingSchedule(performance.now());
      } else if (s === "closed") {
        // The cellar first: it was built from a profile of a session we have
        // been thrown out of.
        if (screen === "hideout") exitHideout();
        // ...and a summary still waiting on a killcam belongs to that session
        // too: it would land on top of the reconnect screen.
        cancelSummaryDefer();
        // Everything derived from the old connection is now meaningless.
        clock.reset();
        interpolator.clear();
        remotes.clear();
        names.clear();
        playerId = "";
        // Including the HUD: showing the health and ammunition of a raid we are
        // no longer in would be worse than showing nothing.
        resetHud();
        // The reconnect's `welcome` states the skills again.
        skillMods = DEFAULT_SKILL_MODS;
        // ...and the meters, for the same reason.
        needsMods = moveModsFromNeeds(FULL_NEEDS);
        refreshMoveMods();
        setDeadUi(false);
        // ...and the loot: an inventory and a ground full of items belonging to
        // a raid we have been disconnected from are pure fiction.
        inventory.reset();
        // `resetHud` clears the store's copy; the mirror must follow.
        showSettings(false);
        droppedItems.clear();
        interact.clearTarget();
        // A stash from a session we were thrown out of cannot be spent. `mode`
        // survives to tell a fresh `profile` from a `welcome` straight back in.
        meta.reset();
        // ...and friends and party; the reconnect's `hello` refreshes both.
        social.reset();
        goScreen("connecting");
        // Reconnect is a loading phase even when the old map is cached: hide
        // the frozen raid and show the actual connection state.
        input.setMapReady(false);
        setScrimStatus("Connecting…");
      }
    });

    socket.on("onWelcome", (msg) => {
      // FIRST: a party leader can deploy while we stand in our own cellar. The
      // world goes back to the void before the raid's is installed, and
      // `exitHideout` unmutes the socket the download depends on.
      if (screen === "hideout") exitHideout();
      playerId = msg.playerId;
      clock.onWelcome(msg.tick, performance.now());
      inventory.syncWeaponSelection();
      // A `welcome` before any `profile` is a direct server; a no-op on a meta
      // one.
      noteMode("direct");

      // The raid supersedes whatever menu screen was up; the map flow below
      // owns
      // the display and `swapIn` finishes the transition.
      setQueue(null);
      cancelSummaryDefer();
      setSummary(null);
      meta.setActive(false);
      goScreen("deploying");

      // Converted ONCE into local time, relative to this welcome's own `tick`.
      setRaidEndsAt(
        msg.raidEndTick === undefined
          ? null
          : performance.now() + ((msg.raidEndTick - msg.tick) / TICK_RATE) * 1000,
      );

      // Set BEFORE the world exists: the first predicted step must be taken by
      // the body the server is stepping. Absent = the neutral body.
      skillMods = msg.skillMods ?? DEFAULT_SKILL_MODS;
      refreshMoveMods();

      // The sky BEFORE the map loads, so the first frame of a night raid is a
      // night — the map load rebuilds the dome from whatever the view holds.
      const env = msg.env ?? DEFAULT_ENV;
      view.setEnv(env);
      audio.setEnv(env);
      townMap.setEnv(env);
      // Each welcome supersedes the last; the token stops a slow old download
      // installing over a newer one.
      const token = ++welcomeToken;

      const swapIn = (next: MapDef): void => {
        if (token !== welcomeToken) return;
        if (next !== map) {
          map = next;
          // Rebuilt from the new map's own boxes, so `collision` and `solids`
          // stay the index-aligned pair the note at boot describes.
          collision = buildMapCollisionWorld(map);
          solids = mapCollisionSolids(map);
          view.loadMap(map);
          // Every pane is whole again; a `glassState` for THIS raid follows.
          glassState.reset();
          tools.setMap(map);
          townMap.setMap(map);
          // Rounds still in the air were fired at geometry that no longer
          // exists; stepping them through the new world is meaningless.
          bullets.clear();
          // Same for the loot: the ground under it belongs to another map, and
          // the server re-sends `worldItems` for the new one.
          droppedItems.clear();
          interact.clearTarget();
        }
        // Always rebase onto the server's spawn, map change or not.
        prediction.respawn(collision, msg.spawn);
        interpolator.clear();
        remotes.clear();
        // There is a world now. Hide the loading layer and enter the raid
        // immediately; a later canvas click captures the mouse when needed.
        input.setMapReady(true);
        setScrimProgress(null);
        goScreen("raid");
      };

      // A cached or static map resolves without a round trip — no one-frame
      // flash.
      if (!mapLoader.isCached(msg.mapSource)) setScrimStatus("Loading map…");

      // The bar only moves for a download that reports a total; a server too
      // old to announce `bytes` gets a KB counter in the title instead.
      const onProgress = (loaded: number, total: number | null): void => {
        if (token !== welcomeToken) return;
        if (total !== null) setScrimProgress(loaded / total);
        else setScrimStatus(`Loading map… ${Math.round(loaded / 1024)} KB`);
      };

      mapLoader.load(msg.mapSource, msg.mapId, onProgress).then(
        async (next) => {
          if (token !== welcomeToken) return;
          // Building the world is another long synchronous block; name the
          // phase and hand the browser a frame first. Unless the map is already
          // installed (a reconnect), where `swapIn` only rebases the spawn.
          if (next !== map) {
            setScrimStatus("Building world…");
            setScrimProgress(null);
            await yieldToDisplay();
            if (token !== welcomeToken) return;
          }
          swapIn(next);
        },
        (err: unknown) => {
          if (token !== welcomeToken) return;
          reportMapFailure(msg.mapId, err);
        },
      );
    });

    socket.on("onSnapshot", (snap) => {
      const now = performance.now();
      clock.onSnapshot(snap.tick, now);
      // A reconnect hands us the old PMC whose replay guard remembers the
      // previous connection; advance the loop past its high-water mark.
      loop.syncInputSeq(snap.lastAckSeq);
      // Injury half of the movement mods, set BEFORE the replay so the
      // unacknowledged tail re-simulates with the penalty the server applied.
      healthMods = moveModsFromLegs(
        (snap.you.hp[LEG_L_HP_INDEX] ?? 0) <= 0 || limbMaskHas(snap.you.fracMask, "legL"),
        (snap.you.hp[LEG_R_HP_INDEX] ?? 0) <= 0 || limbMaskHas(snap.you.fracMask, "legR"),
      );
      // ...and the needs half, at the same moment. Absent = both full.
      needsMods = moveModsFromNeeds(snap.you.needs ?? FULL_NEEDS);
      refreshMoveMods();
      prediction.onSnapshot(snap);
      interpolator.ingest(snap, now);

      for (const e of snap.entities) {
        if (e.name !== undefined && e.name.length > 0) names.set(e.id, e.name);
        // Squad awareness, recorded here because this is where a squadmate
        // exists at all — the strip must still know "Anna" was ours after her
        // corpse leaves the interpolator. Reads `names` a line after it was
        // written.
        if (e.team === true) noteTeammate(e.id, displayName(e.id));
      }

      // Marks on the ground arrive once. `!== undefined` rather than `?.` so
      // the
      // common keyless snapshot wakes nothing. `now` is ms; the field runs on
      // seconds.
      if (snap.tracks !== undefined) view.addTracks(snap.tracks, now / 1000);

      fireControl.onSnapshot(snap, now);
      // `medEndTick` rides on the snapshot: the authoritative end of a med use.
      inventory.onSnapshot(snap, now);
      // ...and so does the extraction dwell. Absent means "not in one", which
      // is what makes the HUD readout appear and disappear on its own.
      setExtract(snap.you.extract ?? null);
      audio.setReloading(snap.you.weapon.reloading);
      // The sound belongs to the action the server is running. Absent = idle.
      audio.setMagChecking(snap.you.weapon.checking === true);
      // `hp` is already a fresh array out of the socket's narrowing.
      pushSnapshot({
        limbs: snap.you.hp,
        bleedMask: snap.you.bleedMask,
        fracMask: snap.you.fracMask,
        dead: snap.you.dead,
        // `null`, not full meters: a server that says nothing has none, and the
        // HUD draws no bars. See `HudState.needs`.
        needs: snap.you.needs ?? null,
      });
      setDeadUi(snap.you.dead);
    });

    socket.on("onEvent", (ev) => {
      const now = performance.now();
      switch (ev.t) {
        case "playerJoined":
          names.set(ev.id, ev.name);
          return;
        case "playerLeft":
          interpolator.remove(ev.id);
          remotes.remove(ev.id);
          names.delete(ev.id);
          return;
        case "shot": {
          // Never our own shot (the server excludes the shooter). `ev.id` keeps
          // the round from clipping the shooter's drawn pose. `weaponId` gives
          // the shooter's muzzle velocity and drop; `undefined` falls through
          // to
          // the carbine default. BASE stats — fitted mods are not public.
          const def = ev.weaponId === undefined ? null : getItemDef(ev.weaponId);
          const stats = def !== null && def.kind === "weapon" ? def.stats : undefined;
          bullets.spawn(ev.origin, ev.dir, ev.id, false, stats);
          // The muzzle POSITION, not a distance: `GameAudio` owns the listener
          // and works out both the range and which ear it belongs in.
          audio.remoteShot(ev.origin, ev.weaponId);
          return;
        }
        case "hit":
          // The authoritative answer to the predicted marker we already showed.
          if (ev.shooterId === playerId) confirmHit(audio, "hit", now);
          // ...and the target's rig jolts. Never ourselves: we have no 3P rig.
          if (ev.targetId !== playerId) {
            remotes.hit(
              ev.targetId,
              ev.limb,
              ev.shooterId === playerId
                ? prediction.state.pos
                : remotes.positionOf(ev.shooterId),
            );
          }
          return;
        case "grenadeThrown":
          // Including our own, unlike `shot`: nothing was drawn when we let go.
          grenades.onThrown(ev, now);
          return;
        case "grenadeExploded":
          // Drives the bang, the light, the smoke and the audio. The position
          // is authoritative; whatever our own arc thought is discarded.
          grenades.onExploded(ev);
          return;
        case "bark":
          // Text only for now; the KIND is what a voice line will key off.
          remotes.bark(ev.id, barkText(ev.kind, ev.v), now);
          return;
        case "glass":
          // Everybody hears it, the breaker included — nothing was predicted.
          glassState.apply(ev.id, true);
          return;
        case "birdFlush":
          // Broadcast to everybody — that is the feature. Both halves on the
          // server's coordinates.
          birds.flush(ev.pos);
          audio.birdFlush(ev.pos);
          return;
        case "itemDropped":
          droppedItems.add({ id: ev.id, defId: ev.defId, pos: ev.pos });
          return;
        case "itemTaken":
          droppedItems.remove(ev.id);
          return;
        case "death":
          // Greys a squad strip line; no-ops for every other death. Your own
          // entity is never `team` to yourself, so this cannot cross out your
          // name.
          noteTeammateDeath(ev.id);
          if (ev.id === playerId) {
            // Everything the death screen names comes off this ONE event.
            const info: DeathInfo = {
              cause: ev.cause,
              killerName:
                ev.killerName ??
                (ev.killerId === playerId ? "" : displayName(ev.killerId)),
              weapon:
                ev.weaponId === undefined
                  ? ""
                  : (getItemDef(ev.weaponId)?.name ?? ev.weaponId),
              limb:
                ev.limb === undefined
                  ? ""
                  : (LIMB_LABELS[LIMB_IDS.indexOf(ev.limb)] ?? ""),
              distM: ev.distM ?? null,
              killcamUntilMs: null,
            };
            // A killcam needs somebody else's eye to sit in; a bleed-out or a
            // starvation has none.
            if (ev.killerEye !== undefined && ev.killerId !== playerId) {
              const untilMs = now + KILLCAM_MS;
              const at = prediction.state.pos;
              // Aimed a little above the feet, so the shot frames the body on
              // the ground rather than the dirt under it.
              const lookAt = { x: at.x, y: at.y + KILLCAM_BODY_Y, z: at.z };
              loop.setKillcam(ev.killerEye, lookAt, untilMs);
              // Otherwise the camera sits inside the killer's skull.
              remotes.setHidden(ev.killerId, true);
              killcam = { killerId: ev.killerId, untilMs };
              info.killcamUntilMs = untilMs;
            }
            setDeath(info);
            fireControl.setDead(true);
            // A dead man's pin is the server's to forget (it destroys the
            // grenade outright — see `killPlayer`); this is the hand it was in.
            grenades.setDead(true);
            // Also drops the open crate, the half-applied bandage and every op
            // still in flight — see `setDeadUi`.
            setDeadUi(true);
            // From the EVENT, not the snapshot flag, which also fires on a
            // refused respawn.
            audio.deathSting();
          } else {
            // Somebody else's death, player or scav: his rig folds up. The
            // killer's eye is the best push origin there is; failing that,
            // wherever we are drawing him. A self-credited bleed-out has no
            // push at all — `null`, so the rig sags rather than toppling away
            // from its own feet.
            remotes.kill(
              ev.id,
              ev.killerEye ??
                (ev.killerId === ev.id
                  ? null
                  : ev.killerId === playerId
                    ? prediction.state.pos
                    : remotes.positionOf(ev.killerId)),
              ev.limb ?? null,
            );
            if (ev.killerId === playerId) confirmHit(audio, "kill", now);
          }
          return;
        default:
          return;
      }
    });

    socket.on("onPong", (msg) => {
      clock.onPong(msg.cNow, msg.sNow, performance.now());
    });

    // Per-recipient rather than an event — nobody else is told who is blind.
    socket.on("onFlash", (sec) => {
      setFlash(sec, performance.now());
      audio.flashRing();
    });

    // --- Inventory / looting (protocol v4) ---------------------------------
    socket.on("onInv", (inv) => {
      inventory.onInv(inv);
    });
    socket.on("onContainerState", (msg) => {
      inventory.onContainerState(msg);
    });
    socket.on("onInvResult", (msg) => {
      // One id space, two controllers: each ignores an id it did not issue.
      inventory.onInvResult(msg);
      meta.onInvResult(msg);
      social.onInvResult(msg);
    });
    socket.on("onWorldItems", (items) => {
      droppedItems.setAll(items);
    });
    socket.on("onGlassState", (broken) => {
      // The backlog: windows broken before we joined. Silent — nobody wants a
      // dozen shatters on spawn.
      for (const id of broken) glassState.apply(id, false);
    });

    // --- Meta / lobby (protocol v6) ----------------------------------------

    socket.on("onProfile", (profile) => {
      // The FIRST message after `hello` on a meta server, which is how the
      // client knows it is on one at all.
      noteMode("meta");
      meta.onProfile(profile);
      // Only the first profile moves the player; later ones update whatever is
      // up.
      if (screen === "connecting") enterMenu();
    });

    socket.on("onQueueStatus", (msg) => {
      // `queued: 0` means "you are in no queue" (the answer to a `queueLeave`);
      // reading it as a queue of nobody would re-enter the panel just
      // cancelled.
      if (msg.queued <= 0) {
        setQueue(null);
        if (screen === "queue") enterMenu();
        return;
      }
      setQueue({ queued: msg.queued, startsInMs: msg.startsInMs });
      // Server-driven, so it also covers the case the client did not start: a
      // reconnect that lands back in a queue we were already in.
      if (screen === "menu" || screen === "connecting") {
        meta.setActive(true);
        goScreen("queue");
      }
    });

    // --- Friends and the party: pushed state that never touches the screen
    // machine.

    socket.on("onFriends", (msg) => {
      social.onFriends(msg);
    });
    socket.on("onPartyState", (party) => {
      social.onPartyState(party);
    });
    socket.on("onPartyInvited", (from) => {
      social.onPartyInvited(from);
    });

    socket.on("onRaidEnd", (summary) => {
      // The scene freezes and the readout goes over it; teardown waits for
      // "Back to menu".
      const apply = (): void => {
        summaryTimer = null;
        setSummary(summary);
        setExtract(null);
        setRaidEndsAt(null);
        inventory.setInventoryOpen(false);
        // Releases the pointer via `applyLockAllowed` — the summary is a button.
        goScreen("summary");
      };
      // ...but a running killcam gets its three seconds first: the summary
      // would cover the one shot that explains it.
      const nowMs = performance.now();
      if (killcam !== null && killcam.untilMs > nowMs) {
        cancelSummaryDefer();
        summaryTimer = window.setTimeout(apply, killcam.untilMs - nowMs);
        return;
      }
      apply();
    });

    socket.on("onServerError", (msg) => {
      console.error(`server error [${msg.code}] ${msg.msg}`);
      // A refused queue join must not leave a countdown that never arrives.
      // Once a `queueStatus` has landed we are demonstrably queued.
      if (screen === "queue" && useHud.getState().queue === null) enterMenu();
    });

    socket.connect();
  }

  // Distance between rendered frames drives bob and footsteps — the
  // distance-not-time trick `character.ts` uses, so all three share one gait.
  let lastFeetX = 0;
  let lastFeetZ = 0;
  let haveLastFeet = false;

  /** Refilled and re-passed every frame — the frame path allocates nothing. */
  const aimCtx: AimContext = { adsT: 0, movedM: 0, crouched: false };
  /** Scratch for the tactical light's eye and view axis, for the same reason. */
  const tacEye = new THREE.Vector3();
  const tacDir = new THREE.Vector3();
  const tacMuzzle = new THREE.Vector3();
  const tacQuat = new THREE.Quaternion();

  const combat: CombatSystems = {
    update(dtMs, nowMs, feet, eyeHeight) {
      // --- Screen keys. Both buffers are drained every frame so a press made
      // while dead is not banked.
      const toggles = input.consumeInventoryToggles();
      const closes = input.consumeMenuCloses();
      const weaponSelection = input.consumeWeaponSelection();
      // Between raids they are dropped: the menu has its own gear screen.
      if (inMetaScreen()) {
        // nothing
      } else if (screen === "hideout") {
        // Escape in the cellar's own priority order (`onEscape`): panel/gym,
        // then the range, then back to the menu. Tab and weapon keys are
        // dropped.
        if (closes > 0 && !hideout.onEscape()) uiBridge.leaveHideout();
      } else if (closes > 0) {
        // Escape in priority order: the bag, the settings overlay, and when
        // neither is up Escape IS the settings key. The store is asked because
        // that is what the overlay renders from.
        if (inventory.isScreenOpen) inventory.setInventoryOpen(false);
        else showSettings(!useHud.getState().settingsOpen);
      } else if (toggles > 0 && !wasDead && !useHud.getState().settingsOpen) {
        // Tab is refused while the settings are up: the bag would open BEHIND
        // the panel, and the next Escape would appear to do nothing.
        inventory.setInventoryOpen(!inventory.isScreenOpen);
      }
      if (weaponSelection !== null && !inMenuLand() && !wasDead) {
        inventory.selectWeapon(weaponSelection);
      }

      let movedM = 0;
      if (haveLastFeet) {
        movedM = Math.hypot(feet.x - lastFeetX, feet.z - lastFeetZ);
      }
      lastFeetX = feet.x;
      lastFeetZ = feet.z;
      haveLastFeet = true;
      // Flying is not a gait: noclipping reads zero distance walked, as
      // `animFor` does.
      if (input.noclip) movedM = 0;

      // The ears before anything that might make a noise: the RENDERED feet,
      // so what is heard agrees with what is seen.
      audio.setListener(feet, input.yaw);

      // No world yet: the trigger and the reach are DRAINED, so arriving does
      // not fire everything clicked while waiting.
      if (screen === "hideout") {
        // The cellar's world is client-built, so `map` is still null here and
        // everything keyed off it correctly does nothing. The session drains
        // the
        // trigger and F; the other three are drained so nothing banked goes off
        // in the next raid.
        input.consumeMagCheckPresses();
        input.consumeGrenadePresses();
        input.consumeGrenadeReleases();
        hideout.update(nowMs, feet, eyeHeight);
      } else if (map === null) {
        input.consumeFirePresses();
        input.consumeInteractPresses();
        input.consumeMagCheckPresses();
        input.consumeGrenadePresses();
        input.consumeGrenadeReleases();
        interact.clearTarget();
      } else {
        fireControl.update(nowMs);
        // After the trigger, like the grenade: a frame with both is a frame the
        // shot happened in. `fireControl` decides whether it becomes a packet.
        if (input.consumeMagCheckPresses() > 0) fireControl.magCheck();
        // After the trigger, so a frame in which both happened resolves the
        // shot first — the same order the server's tick resolves them in.
        grenades.update(dtMs, nowMs);
        // After the remotes are posed (the loop guarantees it) so a corpse can
        // be looted at the position it is actually drawn at.
        interact.update(feet, eyeHeight);
      }

      // The server owns the reload; `fireControl` holds the flag. Before
      // `update`, so a reload that began this frame is drawn this frame.
      viewmodel.setReload(fireControl.isReloading);
      // ...and the magazine check, on the same contract, from the same owner.
      viewmodel.setMagCheck(fireControl.isChecking);
      viewmodel.update(dtMs, movedM, input.adsHeld);
      // After the trigger and unconditionally (dead, menu, no map): sway must
      // keep breathing and recoil recovering. `adsT` comes from the viewmodel —
      // one owner for the shoulder blend.
      aimCtx.adsT = viewmodel.adsT;
      aimCtx.movedM = movedM;
      aimCtx.crouched = prediction.state.stance === "crouch";
      aim.update(dtMs, aimCtx);
      // Rounds fly first, so a bolt claimed by this frame's shot is already
      // placed before `Fx` advances the impacts and the flash.
      bullets.update(dtMs, nowMs);
      fx.update(dtMs);
      birds.update(dtMs);

      // --- The mounted device: four conditions collapse into one `kind` here,
      // where all four are known — latched on, the active gun carries a device,
      // there is a world, the player is alive (the server omits `tac` for a
      // corpse too). `weaponTacticalKind` is derived once per inventory change.
      const tacKind =
        input.tacticalOn && map !== null && !wasDead
          ? inventory.weaponTacticalKind
          : null;
      // The WORLD camera as posed this frame, so the beam leaves where the
      // player looks. Not the fire path's predicted eye: a shot must be
      // reasoned
      // about by the server, a beam only has to agree with the screen.
      const cam = view.camera;
      cam.getWorldPosition(tacEye);
      cam.getWorldDirection(tacDir);
      // The beam's origin is the barrel tip as DRAWN, carried into the world by
      // the same pose.
      viewmodel.muzzleInView(tacMuzzle);
      cam.getWorldQuaternion(tacQuat);
      tacMuzzle.applyQuaternion(tacQuat).add(tacEye);
      tacticalFx.update(tacEye, tacDir, tacMuzzle, tacKind);

      // The killcam's three seconds are up: the loop has already dropped the
      // camera override, this puts the killer back on screen.
      if (killcam !== null && nowMs >= killcam.untilMs) endKillcam();

      const state = prediction.state;
      audio.step(movedM, state.onGround, state.stance === "crouch");
      // Reserve is what the next reload can reach (`InventoryController.
      // refreshEquipped`). The last three are about what the player KNOWS: the
      // gun's from `fireControl`, the spare boxes' from the inventory — all
      // plain field reads.
      setAmmo(
        fireControl.mag,
        fireControl.magSize,
        inventory.reserveRounds,
        fireControl.isReloading,
        fireControl.magKnown,
        fireControl.isChecking,
        inventory.reserveUnknown,
      );
      // Fed the RENDERED feet and the raw look angle, so the arrow on the map
      // is exactly where and how the camera is, not where the last tick was.
      townMap.update(feet, input.yaw);
    },
  };

  const loop = new GameLoop({
    view,
    input,
    prediction,
    remotes,
    clock,
    interpolator,
    overlay,
    debug: tools,
    socket,
    combat,
    aim,
    statusText: () => {
      if (offline) return "offline (prediction only)";
      const who = playerId === "" ? name : `${name}/${playerId}`;
      return `${status} ${serverUrl} as ${who}`;
    },
  });

  window.addEventListener("resize", () => {
    view.resize();
  });

  // Keyboard lock rides with fullscreen (F10 / settings) — see `fullscreen.ts`.
  installKeyboardLock();

  // Ctrl+W cannot be cancelled, but the browser asks first once `beforeunload`
  // objects. Only while there is a raid to lose. `preventDefault` + a non-empty
  // `returnValue` is the cross-browser spelling.
  window.addEventListener("beforeunload", (ev) => {
    if (screen !== "raid" || wasDead) return;
    ev.preventDefault();
    ev.returnValue = "";
  });

  loop.start();
}

// ---------------------------------------------------------------------------

/** The world of an `?offline=1` session. Only static maps qualify. */
function initialMapId(raw: string | null): string {
  const fallback = "testfield";
  if (raw === null || raw.length === 0) return fallback;
  if (hasMap(raw)) return raw;
  console.warn(
    `?map=${raw} is not a static map (only maps compiled into the client can be ` +
      `named here; generated maps arrive from the server); using "${fallback}"`,
  );
  return fallback;
}

function isTruthy(v: string | null): boolean {
  if (v === null) return false;
  return v !== "0" && v.toLowerCase() !== "false";
}

/** Names must be 1..24 printable characters — see `validateClientMsg`. */
function sanitizeName(raw: string | null): string {
  const fallback = `PMC-${Math.floor(Math.random() * 0x10000)
    .toString(16)
    .padStart(4, "0")}`;
  if (raw === null) return fallback;
  // The server rejects control characters outright, so drop them here.
  let cleaned = "";
  for (let i = 0; i < raw.length && cleaned.length < 24; i++) {
    const c = raw.charCodeAt(i);
    if (c < 0x20 || c === 0x7f) continue;
    cleaned += raw[i];
  }
  return cleaned.length === 0 ? fallback : cleaned;
}

function resolveServerUrl(raw: string | null): string {
  if (raw === null || raw.length === 0) return DEFAULT_SERVER_URL;
  if (raw.startsWith("ws://") || raw.startsWith("wss://")) return raw;
  return `ws://${raw}`;
}

/**
 * Fetch the ladder and push it into the store. Fire-and-forget. `httpOriginFor`
 * is the map download's own ws→http translation, reused. Every failure is
 * silence — nothing the player can act on; the board stays as it was.
 */
async function loadLeaderboard(serverUrl: string): Promise<void> {
  try {
    const url = new URL("/leaderboard", httpOriginFor(serverUrl));
    // `credentials: "omit"` like the map download: the board is public and
    // served with `Access-Control-Allow-Origin: *`. No `cache: "no-store"`, so
    // the server's half-minute `Cache-Control` is honoured.
    const res = await fetch(url, { credentials: "omit" });
    if (!res.ok) return;
    const rows = leaderboardRows(await res.json());
    if (rows !== null) setLeaderboard(rows);
  } catch {
    // See above.
  }
}

/**
 * Narrows `GET /leaderboard`'s body. The one thing not narrowed by
 * `asServerMsg`, with one policy difference: an unusable ROW is dropped rather
 * than the page — a leaderboard is a list of independent facts. A body that is
 * not a list is `null`, leaving the last good board up.
 */
function leaderboardRows(body: unknown): LeaderboardRow[] | null {
  if (typeof body !== "object" || body === null) return null;
  const raw = (body as { rows?: unknown }).rows;
  if (!Array.isArray(raw)) return null;

  const rows: LeaderboardRow[] = [];
  for (const entry of raw) {
    if (typeof entry !== "object" || entry === null) continue;
    const row = entry as Record<string, unknown>;
    const name = row["name"];
    if (typeof name !== "string" || name.length === 0 || name.length > 64) continue;
    rows.push({
      name,
      // A level is at least 1: the ladder has no rung under it, and a "Lv 0"
      // on a public board reads as a broken client rather than a new player.
      level: Math.max(1, boardCount(row["level"])),
      xp: boardCount(row["xp"]),
      raids: boardCount(row["raids"]),
      extracts: boardCount(row["extracts"]),
    });
  }
  return rows;
}

/** A counter off an untrusted JSON body: a non-negative integer, or 0. */
function boardCount(x: unknown): number {
  return typeof x === "number" && Number.isFinite(x) && x > 0 ? Math.floor(x) : 0;
}

async function startClient(): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  if (isTruthy(params.get("offline"))) {
    boot();
    return;
  }
  const serverUrl = resolveServerUrl(params.get("server"));
  // A tab open across a redeploy is replaced before it authenticates against a
  // server it may no longer understand.
  if (await reloadIfStale(serverUrl)) return;
  const auth = await authenticateForGame(serverUrl);
  boot(auth.kind === "account" ? auth.account : undefined);
}

void startClient();
